import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import numeral from 'numeral';

import DataTable from '../../../../components/Datatable';
import Timeline from '../../../../components/Timeline';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { multiTrendColumns } from '../columns';
import { METRIC_COLORS } from '../format';

/** The dashed baseline is the same hue as the current line, lightened so it reads as behind it. */
const BASELINE_ALPHA = '66';

/**
 * The daily trend of one metric, drawn by the shared Timeline chart.
 *
 * One metric on one axis at a time: clicks and impressions differ by an order of magnitude and
 * CTR is a fraction, so sharing an axis would flatten whichever series is smaller into the
 * baseline. Periods are aligned by ordinal day rather than by date — that is the only way to lay a
 * window over the one before it, and the only way a year-over-year comparison can line up at all —
 * while the axis is labelled with the current period's real dates and the table below names both,
 * so the alignment never has to be guessed.
 *
 * Days Search Console did not return stay as gaps. Joining across them would draw a line through
 * data that does not exist, so those days are passed as nulls rather than zeroes.
 *
 * Two shapes, one chart. By default it draws one thing over two periods — the current window
 * solid and its baseline dashed behind it. A report comparing *segments* of the same property
 * instead passes `series`, a list of `{ key, label, color, current, baseline }`, and gets one
 * solid line per segment with each one's baseline dashed in its own hue. Both are the same
 * question drawn the same way, so a reader moving between GSC tools reads one chart, and the
 * second shape lives here rather than in whichever report needed it first.
 */
class PeriodTrend extends Component {
    constructor(props) {
        super(props);
        this.searchFunction = this.searchFunction.bind(this);
        this.axisLabel = this.axisLabel.bind(this);
    }

    /** The segments drawn, as a list, whichever of the two shapes the caller passed. */
    series() {
        const { t, series, metric, current, baseline } = this.props;
        if (series?.length) return series;

        // The default is one unnamed segment: the periods are the comparison, so the legend names
        // them rather than naming a thing being compared.
        return [
            {
                key: 'value',
                label: t('tools.gsc.trend.current'),
                baselineLabel: t('tools.gsc.trend.baseline'),
                color: METRIC_COLORS[metric],
                current,
                baseline,
            },
        ];
    }

    /** Whether any segment has a baseline to draw, which is what adds the dashed lines. */
    hasBaseline() {
        return this.series().some((item) => !!item.baseline?.series?.length);
    }

    /** How many rows the chart has: the longest window any segment returned. */
    length() {
        return this.series().reduce(
            (longest, item) =>
                Math.max(
                    longest,
                    item.current?.series?.length ?? 0,
                    item.baseline?.series?.length ?? 0,
                ),
            0,
        );
    }

    /**
     * The days laid side by side on the ordinal day that aligns them, each row carrying every
     * segment's value for that day in both windows.
     *
     * Each segment's values are keyed by its own `key`, so one row holds the whole day and the
     * table can print a column per segment without the rows having to be joined again.
     */
    rows() {
        const { metric } = this.props;
        const series = this.series();
        const length = this.length();

        return Array.from({ length }, (_, index) => {
            const row = {
                id: index + 1,
                day: index + 1,
                currentDate: null,
                baselineDate: null,
            };

            for (const item of series) {
                const currentPoint = item.current?.series?.[index];
                const baselinePoint = item.baseline?.series?.[index];

                // Every segment covers the same window, so the first one that returned a date for
                // this day names it for the row.
                row.currentDate = row.currentDate ?? currentPoint?.date ?? null;
                row.baselineDate = row.baselineDate ?? baselinePoint?.date ?? null;

                row[`${item.key}Returned`] = !!currentPoint?.returned;
                row[`${item.key}Value`] = currentPoint ? currentPoint[metric] : null;
                row[`${item.key}BaselineReturned`] = !!baselinePoint?.returned;
                row[`${item.key}BaselineValue`] = baselinePoint ? baselinePoint[metric] : null;
            }

            // Timeline orders its rows by this field; the current period's calendar is the one the
            // chart is labelled with.
            row.date = row.currentDate ?? row.baselineDate ?? null;

            return row;
        });
    }

    /**
     * CTR is the one metric the chart does not draw in the unit it arrives in: Timeline's tooltip
     * and axis speak in percent, so the fraction is scaled once here rather than being reformatted
     * at every point that reads it.
     */
    chartValue(value) {
        if (value === null || value === undefined) return null;
        return this.props.metric === 'ctr' ? value * 100 : value;
    }

    axisLabel(value) {
        const { metric } = this.props;
        if (metric === 'ctr') return `${numeral(value).format('0.00')}%`;
        if (metric === 'position') return numeral(value).format('0.0');
        return numeral(value).format('0 a');
    }

    /**
     * One solid line per segment, each with its baseline dashed behind it in its own hue.
     *
     * The baseline keeps the segment's colour rather than getting one of its own, so the legend
     * reads as "this thing, then and now" instead of as twice as many things; the dash and the
     * lightening are what separate the windows. Timeline draws chart.js's own legend, so a reader
     * who wants one line at a time can switch the others off there.
     */
    chartData(rows) {
        const { t, metric } = this.props;
        const series = this.series();

        const datasets = series.map((item) => {
            const color = item.color ?? METRIC_COLORS[metric];
            return {
                label: item.label,
                type: 'line',
                data: rows.map((row) => this.chartValue(row[`${item.key}Value`])),
                borderColor: color,
                backgroundColor: color,
                borderWidth: 2.5,
                yAxisID: 'y',
                tension: 0.25,
            };
        });

        for (const item of series) {
            if (!item.baseline?.series?.length) continue;

            const color = item.color ?? METRIC_COLORS[metric];
            datasets.push({
                label:
                    item.baselineLabel ?? t('tools.gsc.trend.baseline_of', { label: item.label }),
                type: 'line',
                data: rows.map((row) => this.chartValue(row[`${item.key}BaselineValue`])),
                borderColor: `${color}${BASELINE_ALPHA}`,
                backgroundColor: 'transparent',
                borderWidth: 2,
                borderDash: [6, 5],
                yAxisID: 'y',
                tension: 0.25,
            });
        }

        return {
            labels: rows.map((row) =>
                row.currentDate ? moment(row.currentDate).format('DD MMM') : `${row.day}`,
            ),
            datasets,
        };
    }

    searchFunction(nodes, search) {
        if (!search) return nodes;
        const term = search.toLowerCase();
        return nodes.filter(
            (row) =>
                `${row.day}` === term ||
                row.currentDate?.includes(term) ||
                row.baselineDate?.includes(term),
        );
    }

    render() {
        const { t, metric, metricLabel, fileName } = this.props;

        const series = this.series();
        const hasBaseline = this.hasBaseline();
        const rows = this.rows();
        const columns = multiTrendColumns(t, metric, series, { hasBaseline });
        const label = metricLabel ?? t(`tools.gsc.metrics.${metric}`);

        // The windows are the same for every segment, so the first one speaks for the footer:
        // what day 1 lines up with, and how many dates each window covers.
        const first = series[0];
        const currentDates = first.current?.series?.length ?? 0;
        const baselineDates = first.baseline?.series?.length ?? 0;
        const firstCurrent = first.current?.series?.[0]?.date;
        const firstBaseline = first.baseline?.series?.[0]?.date;

        return (
            <div className="gsc-tool-trend">
                <Timeline
                    data={rows}
                    dontShowMetrics
                    chartSize="lg"
                    header={label}
                    extraBox={
                        <p className="gsc-tool-sub chart-subtitle">
                            {t('tools.gsc.trend.one_axis')}
                        </p>
                    }
                    chartContainerStyles="gsc-tool-chart-heading"
                    chartData={this.chartData(rows)}
                    leftAxisLabel={label}
                    leftAxisReversed={metric === 'position'}
                    customYLabelType={this.axisLabel}
                    showLabelAsPercentage={metric === 'ctr'}
                />

                <div className="gsc-tool-chart-foot">
                    <span>
                        {firstBaseline
                            ? t('tools.gsc.trend.day_one', {
                                  current: moment(firstCurrent).format('DD MMM YYYY'),
                                  baseline: moment(firstBaseline).format('DD MMM YYYY'),
                              })
                            : t('tools.gsc.trend.hover')}
                    </span>
                    <span>
                        {t('tools.gsc.trend.date_counts', {
                            current: currentDates,
                            baseline: baselineDates,
                        })}
                    </span>
                </div>

                <details className="gsc-tool-details">
                    <summary>{t('tools.gsc.trend.view_table')}</summary>
                    <DataTable
                        nodes={rows}
                        COLUMNS={columns}
                        sortFns={getSortKeys(columns)}
                        additionalStyles={setAdditionalStyles(columns)}
                        searchFunction={this.searchFunction}
                        fileName={`${fileName ?? t('tools.gsc.trend.title')} — ${label}`}
                        notUseDefaultStyle
                        customContainerStyles="gsc-tool-trend-table"
                    />
                </details>
            </div>
        );
    }
}

export default withTranslation()(PeriodTrend);
